import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { WalletBalance, WalletExpiringSummary } from '@/lib/types';

export interface TopUpPackage {
  nameEn?: string;
  nameAr?: string;
  name?: string;
  descriptionEn?: string;
  descriptionAr?: string;
  description?: string;
  price: number;
  credit: number;
  active: boolean;
}

export function useWalletBalance() {
  return useQuery({
    queryKey: ['/wallets/balance'],
    queryFn: async (): Promise<WalletBalance | null> => {
      const token = apiClient.getToken();
      if (!token) return null;

      try {
        const response = await apiClient.get('/wallets/balance');
        if (!response.ok) {
          if (response.status === 401 || response.status === 404) return null;
          return null;
        }
        return response.json();
      } catch {
        return null;
      }
    },
    enabled: !!apiClient.getToken(),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useExpiringSummary() {
  return useQuery({
    queryKey: ['/wallets/expiring'],
    queryFn: async (): Promise<WalletExpiringSummary | null> => {
      const token = apiClient.getToken();
      if (!token) return null;

      try {
        const response = await apiClient.get('/wallets/expiring');
        if (!response.ok) return null;
        return response.json();
      } catch {
        return null;
      }
    },
    enabled: !!apiClient.getToken(),
    staleTime: 0,
    gcTime: 0,
  });
}

/** Fetch active wallet top-up packages. */
export function useTopUpPackages() {
  return useQuery({
    queryKey: ['/wallets/topup-packages'],
    queryFn: async (): Promise<TopUpPackage[]> => {
      const response = await apiClient.get('/wallets/topup-packages');
      if (!response.ok) return [];
      const json = await response.json();
      return Array.isArray(json) ? json : [];
    },
    enabled: !!apiClient.getToken(),
  });
}

/** Initiate a wallet top-up via the payment gateway. */
export function useTopUpWallet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      packageIndex,
      paymentMethod,
      packageId,
    }: {
      packageIndex: number;
      paymentMethod: string;
      /** Preferred over packageIndex — an index can shift if the list changes. */
      packageId?: string;
    }): Promise<{ paymentLink: string }> => {
      const response = await apiClient.post('/wallets/topup', {
        packageIndex,
        paymentMethod,
        packageId,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to initiate top-up');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/wallets/balance'] });
    },
  });
}
